import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import type { DocumentNode } from "graphql";
import { type Client, type Sink, createClient } from "graphql-ws";
import { config } from "~/config";
import { auth } from "~/services/firebase";
import { setAuthToken, setProfile } from "~/stores/auth";
import { completeLoader, registerLoader } from "~/stores/loader";
import { combineURL } from "~/util/helpers";
import { Subscription } from "./subscription";

export type ConnectionState = "disconnected" | "connecting" | "connected";

/**
 * The App Platform connector
 */
export class PlatformSocket {
	private client?: Client;
	private state: ConnectionState = "disconnected";
	private queries = new Set<Subscription>();
	private subscriptionEndpoint!: string;
	private timeout!: ReturnType<typeof setTimeout>;
	private retryTimeout!: ReturnType<typeof setTimeout>;
	private token: string | undefined;

	public readonly isAvailable: boolean;

	public constructor() {
		console.log("Initializing Platform Service");

		registerLoader("platform");

		// stop initializing if config has no endpoint
		if (!config.platform?.endpoint) {
			this.isAvailable = false;
			return;
		}

		const platformUrl = combineURL(config.platform.endpoint.replace("http", "ws"), "subscriptions");

		this.subscriptionEndpoint = platformUrl;

		this.isAvailable = true;

		auth.authStateReady().then(async () => {
			if (auth.currentUser) {
				setProfile(auth.currentUser);

				const token = await auth.currentUser.getIdToken();

				setAuthToken(token);
				this.token = token;
				this.openConnection(this.subscriptionEndpoint);
			} else {
				this.openConnection(this.subscriptionEndpoint);
			}

			onAuthStateChanged(auth, (user) => {
				setProfile(user);
			});

			onIdTokenChanged(auth, async (user) => {
				if (user) {
					const token = await user.getIdToken();

					if (token !== this.token) {
						setAuthToken(token);
						this.token = token;

						this.openConnection(this.subscriptionEndpoint);
					}
				} else {
					if (this.token) {
						setAuthToken(undefined);
						this.token = undefined;

						this.openConnection(this.subscriptionEndpoint);
					}
				}
			});
		});
	}

	/**
	 * Open a new connection to the given URL
	 *
	 * @param url The URL
	 */
	private openConnection(url: string) {
		this.close();
		this.client = createClient({
			url: url,
			lazy: false,
			keepAlive: 15_000,
			retryAttempts: -1,
			shouldRetry: () => true,
			connectionParams: {
				"Yaa-Source": "widgets",
				Authorization: this.token ? `Bearer ${this.token}` : undefined,
			},
			on: {
				connecting: () => {
					this.state = "connecting";
				},
				connected: () => {
					console.log("Connected");
					this.state = "connected";
					this.resubscribe();

					completeLoader("platform");
				},
				closed: () => {
					console.log("Disconnected");

					clearTimeout(this.retryTimeout);

					this.state = "disconnected";

					this.retryTimeout = setTimeout(() => {
						if (this.state === "disconnected") {
							this.openConnection(this.subscriptionEndpoint);
						}
					}, 5_000);
				},
				ping: (recieved) => {
					if (!recieved) {
						// Close the socket connection if the server doesn't respond to the ping in 5 seconds
						this.timeout = setTimeout(() => {
							if (this.state === "connected") {
								console.warn("Server did not respond to ping in time, retrying connection");

								this.openConnection(this.subscriptionEndpoint);
							}
						}, 5_000);
					}
				},
				pong: (recieved) => {
					if (recieved) {
						clearTimeout(this.timeout);
					}
				},
			},
		});
	}

	/**
	 * Resubscribe all queries
	 */
	private resubscribe() {
		if (!this.client) {
			console.warn("Client not available for resubscription");

			return;
		}

		for (const query of this.queries) {
			query.listen(this.client);
		}
	}

	/**
	 * Executes the given query and listens to the responses.
	 *
	 * @param query The query to execute
	 * @param variables The query variables
	 * @returns Subscription disposer
	 */
	public subscribe(
		query: DocumentNode | string,
		observer: Partial<Sink<any>>,
		variables?: any,
	): () => void | undefined {
		if (!this.isAvailable) {
			throw new Error("App Platform not available");
		}

		const subscription = new Subscription(query, observer, variables, () => {
			this.queries.delete(subscription);
		});

		this.queries.add(subscription);

		if (this.client && this.state === "connected") {
			subscription.listen(this.client);
		}

		return () => {
			subscription.dispose();
		};
	}

	/**
	 * Executes the given query and returns the result
	 *
	 * @param query The query to execute
	 * @param variables The query variables
	 */
	public async query<T = any>(query: DocumentNode | string, variables?: any): Promise<T> {
		if (!this.isAvailable) {
			throw new Error("App platform not available");
		}

		return new Promise<T>((resolve, reject) => {
			let result: any = null;

			this.subscribe(
				query,
				{
					next(value) {
						if (result) {
							// throw new Error('Query returned multiple results');
							return;
						}

						result = value;
					},
					error(error) {
						reject(error);
					},
					complete() {
						resolve(result);
					},
				},
				variables,
			);
		});
	}

	public close() {
		this.client?.dispose();
		this.client = undefined;
	}
}

export const platform = new PlatformSocket();
